import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import { useTriggerById } from "@/shared/domain/actions/hooks";
import { useTriggerV2ById } from "@/shared/domain/triggers/hooks";
import { RobotoNotFoundException } from "@/shared/http";

import { TriggerNameLink } from "./TriggerNameLink";

interface TriggerLinkProps {
  triggerId: string;
}

/**
 * Resolves a trigger id to a name link. The unified read
 * (GET /v1/triggers/id/<id>) answers for anything event-driven — both engine
 * generations — so it runs first; only a definitive not-found falls back to
 * the legacy view, which still serves scheduled triggers.
 */
export function TriggerLink({ triggerId }: TriggerLinkProps) {
  const v2Result = useTriggerV2ById(triggerId);
  const v2NotFound =
    v2Result.isError && v2Result.error instanceof RobotoNotFoundException;
  const viewResult = useTriggerById(v2NotFound ? triggerId : undefined);

  if (v2Result.isPending || (v2NotFound && viewResult.isPending)) {
    return (
      <Typography variant="body2">
        <Skeleton variant="text" width="200px" />
      </Typography>
    );
  }

  if (v2Result.data) {
    return <TriggerNameLink triggerName={v2Result.data.name} />;
  }

  if (v2NotFound && viewResult.data) {
    return <TriggerNameLink triggerName={viewResult.data.name} />;
  }

  if (
    v2NotFound &&
    viewResult.isError &&
    viewResult.error.name === "RobotoNotFoundException"
  ) {
    return (
      <Typography
        color="GrayText"
        sx={{ textDecorationLine: "line-through" }}
        title="This trigger no longer exists"
        variant="body2"
      >
        {triggerId}
      </Typography>
    );
  }

  return (
    <Typography
      color="error"
      title="Failed to load link to trigger"
      variant="body2"
    >
      {triggerId}
    </Typography>
  );
}
